import type { AIModel } from "@/types";
import TinyLlamaWebGpuRuntime, { GenerateOptions } from "@/llm/tinyllamaWebGpuRuntime";
import { normalizeWebLlmModelId } from "@/llm/webllmModelRegistry";

export type LocalLLMState =
  | "idle"
  | "webgpu_not_supported"
  | "downloading"
  | "initializing"
  | "generating"
  | "ready"
  | "error";

export type LocalLLMStatus = {
  state: LocalLLMState;
  progress?: { loaded: number; total: number; stage?: string };
  error?: string;
  warning?: string;
  errorCode?: "shader_f16_unsupported";
  modelId?: string;
  modelLib?: string;
  webGpuInfo?: { adapterName?: string; limits?: any; shaderF16Supported?: boolean };
  diagnostics?: {
    hasWebGPU: boolean;
    crossOriginIsolated: boolean;
    secureContext: boolean;
    protocol: string;
    isIframe: boolean;
    isElectron: boolean;
  };
};

type Config = { modelId?: string };
type LocalModelInput = string | Partial<AIModel>;

const FALLBACK_MODELS = [
  "Llama-3.2-1B-Instruct-q4f32_1-MLC",
  "Phi-3-mini-4k-instruct-q4f32_1-MLC",
  "Qwen2-0.5B-Instruct-q4f32_1-MLC",
];
const SHADER_STALL_TIMEOUT_MS = 15000;

function getProgressState(stage?: string): LocalLLMState {
  const text = (stage || "").toLowerCase();
  if (!text) return "downloading";
  if (
    text.includes("shader") ||
    text.includes("initializ") ||
    text.includes("compile") ||
    text.includes("prefill") ||
    text.includes("load wasm") ||
    text.includes("finish loading")
  ) {
    return "initializing";
  }
  return "downloading";
}

function isElectronEnvironment() {
  return typeof window !== "undefined" && Boolean((window as any).electronAPI?.isElectron);
}

function buildShaderF16Warning(modelId: string | undefined, shaderF16Supported: boolean | undefined) {
  if (shaderF16Supported !== false) {
    return undefined;
  }

  if (!modelId?.toLowerCase().includes("q4f16_1")) {
    return undefined;
  }

  return "This device/runtime does not support shader-f16. MapleOS will try a q4f32 compatibility variant when available.";
}

function isShaderF16UnsupportedError(error: unknown) {
  const message = error instanceof Error ? error.message : String(error || "");
  return message.toLowerCase().includes("extension 'f16' is not allowed in the current environment");
}

export class LocalTinyLlamaManager {
  private runtime: TinyLlamaWebGpuRuntime | null = null;
  private status: LocalLLMStatus = { state: "idle" };
  private listeners = new Set<(s: LocalLLMStatus) => void>();
  private config: Config;
  private activeModelId: string | null = null;
  private initPromise: Promise<void> | null = null;
  private initTargetModelId: string | null = null;
  private activeGenerationCount = 0;
  private stallTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(config: Config = {}) {
    this.config = config;
  }

  getStatus(): LocalLLMStatus {
    return this.status;
  }

  getActiveModelId() {
    return this.activeModelId;
  }

  subscribe(listener: (status: LocalLLMStatus) => void): () => void {
    this.listeners.add(listener);
    listener(this.status);
    return () => this.listeners.delete(listener);
  }

  private setStatus(next: LocalLLMStatus) {
    this.status = next;
    this.listeners.forEach((fn) => fn(next));
  }

  private clearStallTimer() {
    if (this.stallTimer) {
      clearTimeout(this.stallTimer);
      this.stallTimer = null;
    }
  }

  private scheduleShaderStallWarning(stage: string | undefined) {
    this.clearStallTimer();

    if (!isElectronEnvironment()) {
      return;
    }

    const normalizedStage = String(stage || "").toLowerCase();
    if (!normalizedStage.includes("shader")) {
      return;
    }

    this.stallTimer = setTimeout(() => {
      const currentStage = this.status.progress?.stage || stage;
      const warning = "GPU shader compilation appears to be taking unusually long in Electron. This is usually runtime/GPU initialization, not a model download.";

      console.warn("[local-llm] shader compilation appears stalled in Electron", {
        modelId: this.status.modelId,
        stage: currentStage,
        diagnostics: this.status.diagnostics,
        webGpuInfo: this.status.webGpuInfo,
      });

      this.setStatus({
        ...this.status,
        warning,
      });
    }, SHADER_STALL_TIMEOUT_MS);
  }

  private setGeneratingStatus() {
    if (!this.activeModelId) return;
    this.clearStallTimer();
    this.setStatus({
      state: "generating",
      modelId: this.activeModelId,
      modelLib: this.status.modelLib,
      webGpuInfo: this.status.webGpuInfo,
      diagnostics: this.status.diagnostics,
      warning: this.status.warning,
    });
  }

  private restoreReadyStatus() {
    if (!this.activeModelId) return;
    this.clearStallTimer();
    this.setStatus({
      state: "ready",
      modelId: this.activeModelId,
      modelLib: this.status.modelLib,
      webGpuInfo: this.status.webGpuInfo,
      diagnostics: this.status.diagnostics,
      warning: this.status.warning,
    });
  }

  private buildCandidateList(requested?: LocalModelInput) {
    if (requested) {
      return [requested];
    }

    const configuredId = this.config.modelId ? normalizeWebLlmModelId(this.config.modelId) : undefined;
    const unique = new Map<string, LocalModelInput>();

    if (configuredId) unique.set(configuredId, configuredId);
    FALLBACK_MODELS.forEach((modelId) => {
      const normalized = normalizeWebLlmModelId(modelId);
      if (!unique.has(normalized)) unique.set(normalized, normalized);
    });

    return Array.from(unique.values());
  }

  private async tryInitWithModel(model: LocalModelInput, diag: LocalLLMStatus["diagnostics"], info?: LocalLLMStatus["webGpuInfo"]) {
    const modelId = normalizeWebLlmModelId(model);

    if (!this.runtime) {
      this.runtime = new TinyLlamaWebGpuRuntime(model);
    }

    console.info("[local-llm] init start", {
      modelId,
      diagnostics: diag,
      webGpuInfo: info,
    });

    let lastProgressKey = "";
    const compatibilityWarning = buildShaderF16Warning(modelId, info?.shaderF16Supported);

    await this.runtime.init({
      model,
      onProgress: (loaded, total, stage) => {
        const progressState = getProgressState(stage);
        const progressKey = `${progressState}:${loaded}:${stage || ""}`;

        if (progressKey !== lastProgressKey) {
          lastProgressKey = progressKey;
          console.info("[local-llm] init progress", {
            modelId,
            loaded,
            total,
            stage,
            progressState,
          });
        }

        this.scheduleShaderStallWarning(stage);
        this.setStatus({
          state: progressState,
          progress: { loaded, total, stage },
          modelId,
          webGpuInfo: info,
          diagnostics: diag,
          warning: compatibilityWarning,
        });
      },
    });

    this.clearStallTimer();

    const resolved = this.runtime.getResolvedModel();
    this.activeModelId = resolved.modelId;

    console.info("[local-llm] init ready", {
      modelId: resolved.modelId,
      modelLib: resolved.modelLibCandidates[0],
    });

    this.setStatus({
      state: "ready",
      modelId: resolved.modelId,
      modelLib: resolved.modelLibCandidates[0],
      webGpuInfo: info,
      diagnostics: diag,
      warning: compatibilityWarning,
    });
  }

  async init(model?: LocalModelInput): Promise<void> {
    const requestedModelId = model ? normalizeWebLlmModelId(model) : undefined;
    if (requestedModelId && (this.status.state === "ready" || this.status.state === "generating") && this.activeModelId === requestedModelId) {
      return;
    }

    if (this.initPromise) {
      if (!requestedModelId || requestedModelId === this.initTargetModelId || requestedModelId === this.activeModelId) {
        return this.initPromise;
      }
      await this.initPromise;
      if ((this.status.state === "ready" || this.status.state === "generating") && this.activeModelId === requestedModelId) {
        return;
      }
    }

    const targetModelId = requestedModelId || this.config.modelId || null;

    const runInit = async () => {
      const diag = {
        hasWebGPU: TinyLlamaWebGpuRuntime.isWebGpuSupported(),
        crossOriginIsolated: (globalThis as any).crossOriginIsolated === true,
        secureContext: (globalThis as any).isSecureContext === true,
        protocol: typeof location !== "undefined" ? location.protocol : "unknown",
        isIframe: typeof window !== "undefined" ? window.self !== window.top : false,
        isElectron: isElectronEnvironment(),
      };

      if (!diag.hasWebGPU) {
        this.setStatus({ state: "webgpu_not_supported", diagnostics: diag });
        return;
      }

      const info = await TinyLlamaWebGpuRuntime.getWebGpuInfo();
      console.info("[local-llm] webgpu capabilities", {
        adapterName: info?.adapterName,
        shaderF16Supported: info?.shaderF16Supported,
        diagnostics: diag,
        limits: info?.limits,
      });
      if (info?.shaderF16Supported === false) {
        console.warn("[local-llm] shader-f16 unsupported on this WebGPU runtime; q4f32 compatibility path will be preferred when available");
      }
      this.setStatus({
        state: "downloading",
        modelId: requestedModelId,
        webGpuInfo: info,
        diagnostics: diag,
        warning: buildShaderF16Warning(requestedModelId, info?.shaderF16Supported),
      });

      const candidates = this.buildCandidateList(model);
      let lastErr: any = null;

      for (const candidate of candidates) {
        try {
          this.setStatus({
            state: "initializing",
            modelId: normalizeWebLlmModelId(candidate),
            webGpuInfo: info,
            diagnostics: diag,
            warning: buildShaderF16Warning(normalizeWebLlmModelId(candidate), info?.shaderF16Supported),
          });
          await this.tryInitWithModel(candidate, diag, info);
          return;
        } catch (err: any) {
          console.error("[local-llm] init failed for candidate", {
            modelId: normalizeWebLlmModelId(candidate),
            error: err,
          });
          lastErr = err;
        }
      }

      this.clearStallTimer();
      this.setStatus({
        state: "error",
        error: isShaderF16UnsupportedError(lastErr)
          ? "This WebGPU runtime does not support shader-f16 for the selected model variant. Try a q4f32 compatibility build or a different runtime."
          : lastErr?.message || String(lastErr),
        errorCode: isShaderF16UnsupportedError(lastErr) ? "shader_f16_unsupported" : undefined,
        modelId: requestedModelId,
        webGpuInfo: info,
        diagnostics: diag,
        warning: buildShaderF16Warning(requestedModelId, info?.shaderF16Supported),
      });
      throw lastErr;
    };

    const promise = runInit();
    this.initPromise = promise;
    this.initTargetModelId = targetModelId;

    try {
      await promise;
    } finally {
      if (this.initPromise === promise) {
        this.initPromise = null;
        this.initTargetModelId = null;
      }
    }
  }

  isReady(model?: LocalModelInput): boolean {
    if (this.status.state !== "ready" && this.status.state !== "generating") {
      return false;
    }

    if (!model) {
      return true;
    }

    return this.activeModelId === normalizeWebLlmModelId(model);
  }

  async generate(options: GenerateOptions): Promise<string> {
    if (this.initPromise) {
      await this.initPromise;
    }
    if (!this.runtime || !this.isReady()) {
      throw new Error("Local LLM is not ready");
    }

    this.activeGenerationCount += 1;
    this.setGeneratingStatus();

    try {
      return await this.runtime.generate(options);
    } catch (error) {
      this.setStatus({
        state: "error",
        error: error instanceof Error ? error.message : String(error),
        modelId: this.activeModelId || undefined,
        modelLib: this.status.modelLib,
        webGpuInfo: this.status.webGpuInfo,
        diagnostics: this.status.diagnostics,
        warning: this.status.warning,
      });
      throw error;
    } finally {
      this.activeGenerationCount = Math.max(0, this.activeGenerationCount - 1);
      if (this.activeGenerationCount === 0 && this.runtime && this.activeModelId) {
        this.restoreReadyStatus();
      }
    }
  }

  async dispose(): Promise<void> {
    this.clearStallTimer();
    await this.runtime?.dispose();
    this.runtime = null;
    this.activeModelId = null;
    this.initPromise = null;
    this.initTargetModelId = null;
    this.activeGenerationCount = 0;
    this.setStatus({ state: "idle" });
  }
}

export const localLLM = new LocalTinyLlamaManager({ modelId: "Llama-3.2-1B-Instruct-q4f32_1-MLC" });